import { useApplication } from "@ca-dmv-radv/data";
import {
  aidEnabledCatDocIds,
  multipPageCatDocIds,
  isDocument,
  Documents,
} from "@ca-dmv-radv/screens/src/dashboard/helpers/constants";
import { useMounted } from "@ca-dmv-radv/utilities";
import InitializeBBY from "@ca-dmv-radv/utilities/src/abby-util";
import { useCallback, useEffect, useMemo, useState } from "react";
import { isMobile } from "react-device-detect";

import { isAIDCapture } from "@ca-dmv-radv/screens/src/dashboard/PresenceVerification/constants";
import useAIDCapture from "./useAIDCapture";

const mwcConfig = {
  startPage: 0,
  enableAutoCapture: true,
  enableAutoCrop: true,
  enableCamera: true,
  minCameraResolution: 500,
  enableSharpnessFilter: true,
  enableQualityAssessmentForOCR: true,
  images: [],
  mwcLocalizedStrings: {},
  useSystemCamera: false,
};

async function blobtoFile(blob, filename, type = "images/png") {
  return {
    name: filename,
    original: new File([blob], filename, { type }),
    type,
  };
}

// global variable so useABBYY behaves like a singleton
let isLoadedOnce = false;

export const CAPTURING_PHOTOS_IDLE = "idle";
export const CAPTURING_PHOTOS_PROCESSING = "processing";
export const CAPTURING_PHOTOS_SUCCESS = "success";

export default function useABBYY() {
  const [capturedImages, setCapturedImages] = useState([]);
  const [isCapturing, setIsCapturing] = useState(CAPTURING_PHOTOS_IDLE);
  const [abbyyLoadingError, setAbbyLoadingError] = useState(null);
  const mounted = useMounted();
  const { documentTypes } = useApplication();

  useEffect(() => {
    if (isLoadedOnce || !isMobile || !mounted) {
      return;
    }

    // prevent script/styles from loading multiple times.
    isLoadedOnce = true;

    InitializeBBY(setAbbyLoadingError);
  }, [isMobile, mounted]);

  const handleWebCapture = useCallback(
    async (chosenDocument) => {
      if (!isLoadedOnce) {
        throw new Error("Mobile Web Capture could not be initialized.");
      }

      if (!isMobile || !mounted || isCapturing !== CAPTURING_PHOTOS_IDLE) {
        return false;
      }

      setIsCapturing(CAPTURING_PHOTOS_PROCESSING);

      mwcConfig.images = capturedImages;
      const fileList = [];
      if (chosenDocument.multiplePages === false) {
        mwcConfig.maxImagesCount = 1;
      } else if (chosenDocument.requiredPages) {
        mwcConfig.maxImagesCount = chosenDocument.requiredPages.length;
      } else {
        mwcConfig.maxImagesCount = 10;
      }

      if (
        // return the WebCapture Promise for image processing
        isAIDCapture(chosenDocument.ATD) &&
        aidEnabledCatDocIds.includes(chosenDocument.catDocId)
      ) {
        let aidImageCount = 1;

        if (chosenDocument.multiplePages === true) {
          aidImageCount = chosenDocument.requiredPages.length;
        }

        const aidImages = new Array(aidImageCount).fill();

        if (
          isDocument(Documents.PASSPORT, documentTypes[chosenDocument.catDocId])
        ) {
          if (chosenDocument.page === "front" && chosenDocument.accessCamera) {
            const documentSettings = new DocumentSettings();
            documentSettings.documentType = "Passport";
            documentSettings.documentSide = "Front";
            documentSettings.captureTimeout = 60000;
            const aidImage = await useAIDCapture(
              "PASSPORT",
              documentSettings
            ).catch((errorCode) => {
              throw new Error(errorCode);
            });
            if (aidImage) {
              const [frontImage] = aidImage;
              chosenDocument.isFront = true;
              chosenDocument.frontImage = frontImage;

              aidImages[0] = chosenDocument.frontImage;
            }
          } else {
            return;
          }

          chosenDocument.accessCamera = false;
        } else if (multipPageCatDocIds.includes(chosenDocument.catDocId)) {
          if (chosenDocument.page === "both" && chosenDocument.accessCamera) {
            const documentSettings = new DocumentSettings();
            documentSettings.documentType = "License";
            documentSettings.documentSide = "Both";
            documentSettings.captureTimeout = 60000;
            const aidImagesResult = await useAIDCapture(
              "CUSTOM_DOCUMENT",
              documentSettings
            ).catch((errorCode) => {
              throw new Error(errorCode);
            });
            if (aidImagesResult) {
              const [frontImage, backImage] = aidImagesResult;

              chosenDocument.isBoth = true;
              chosenDocument.frontImage = frontImage;
              chosenDocument.backImage = backImage;

              aidImages[0] = chosenDocument.frontImage;
              aidImages[1] = chosenDocument.backImage;
            }
          } else {
            return;
          }

          chosenDocument.accessCamera = false;
        } else {
          return;
        }

        fileList[0] = aidImages;

        return Promise.all(fileList[0]);
      }

      const images = await window.ABBYY.WebCapture.capture(mwcConfig);

      if (images.length === 0) {
        // setIsCapturing(CAPTURING_PHOTOS_IDLE);
        return false;
      }

      // stash images in case user doesn't upload required images
      setCapturedImages(images);

      // convert each image (blob) to a File
      for (let i = 0; i < images.length; i += 1) {
        const image = images[i];
        const filename = `mwc-${new Date().getTime()}.jpg`;
        const filePromise = blobtoFile(image.capturedImage, filename);
        fileList[i] = filePromise;
      }

      if (
        !chosenDocument.requiredPages ||
        (chosenDocument.requiredPages &&
          images.length === chosenDocument.requiredPages.length)
      ) {
        setIsCapturing(CAPTURING_PHOTOS_SUCCESS);
      }

      // return the fileList for further processing
      return Promise.all(fileList);
    },
    [capturedImages, isCapturing]
  );
  return useMemo(
    () => ({
      handleWebCapture,
      setCapturedImages,
      setIsCapturing,
      setAbbyLoadingError,
      abbyyLoadingError,
      hasLoadedABBYY: isLoadedOnce,
      isCapturing,
    }),
    [handleWebCapture, abbyyLoadingError, isLoadedOnce, isCapturing]
  );
}
